import { memo, useCallback, useMemo } from "react"

import { CreateUpdateMeetingServiceOrder, CreateOrderFlowType, AvailableModules } from "../../createOrderTypes"

import { useCreateOrderDialogType } from "../../hooks/useCreateOrderDialogType"
import { useCreateOrderFormConfig } from "../../mainForm/CreateOrderFormContext"
import { FinalSuccessDialogContent } from "./FinalSuccessDialogContent"
import { OrderFormStatus } from "../../mainForm/helpers"
import { CreateOrderType } from "../../helpers"

type FinalSuccessDialogMeetingServiceProps = Readonly<{
    orderDetails: CreateUpdateMeetingServiceOrder
    onClose: () => void
    handleCloseMainDialog: () => void
    handleAddNewMeetingService: () => void
    handleOpenResourceBooking: (type: CreateOrderType) => void
    orderFormStatusRef: React.MutableRefObject<OrderFormStatus>
    availableModules: AvailableModules
    orderId?: number
    savedAsUpdate?: boolean
    seriesOrderCount?: number
}>

export const FinalSuccessDialogMeetingService = memo(function FinalSuccessDialogMeetingService({
    orderDetails,
    onClose,
    handleCloseMainDialog,
    handleAddNewMeetingService,
    handleOpenResourceBooking,
    orderFormStatusRef,
    availableModules,
    orderId,
    savedAsUpdate,
    seriesOrderCount,
}: FinalSuccessDialogMeetingServiceProps) {
    const { isFullInitOrder, resourceBookingId } = orderFormStatusRef.current
    const { flowConfig } = useCreateOrderFormConfig()
    const isOutlookFlow = flowConfig?.flow === CreateOrderFlowType.OUTLOOK
    const { isSpecialOrder } = useCreateOrderDialogType()
    const hasVstoFlowParam = Boolean(flowConfig?.vstoFlowParam)

    // rules:
    // 1) if isSpecialOrder — both actions are disabled
    // 2) if isOutlookFlow — do not show "New service"
    // 3) if vstoFlowParam exists — do not show "Add resource booking"
    // 4) if module is not active — do not show the action
    const canShowResourceBooking = !isSpecialOrder && !hasVstoFlowParam && availableModules.isBookingActive
    const canShowNewService = !isSpecialOrder && !isOutlookFlow

    const wrapWithClose = useCallback(
        (fn: () => void) => () => {
            onClose()
            fn()
        },
        [onClose],
    )

    const handleFinish = useMemo(() => wrapWithClose(handleCloseMainDialog), [wrapWithClose, handleCloseMainDialog])

    const handleAddResourceBooking = useMemo(
        () =>
            canShowResourceBooking
                ? wrapWithClose(() => handleOpenResourceBooking(CreateOrderType.RESOURCE_BOOKING))
                : undefined,
        [canShowResourceBooking, wrapWithClose, handleOpenResourceBooking],
    )

    const handleNewService = useMemo(
        () => (canShowNewService ? wrapWithClose(handleAddNewMeetingService) : undefined),
        [canShowNewService, wrapWithClose, handleAddNewMeetingService],
    )

    return (
        <FinalSuccessDialogContent
            open={Boolean(orderDetails)}
            onClose={onClose}
            orderType={CreateOrderType.MEETING_SERVICE}
            handleFinish={handleFinish}
            handleAddResourceBooking={handleAddResourceBooking}
            handleNewService={handleNewService}
            hideClose
            hideTitle={isSpecialOrder}
            isUpdate={isFullInitOrder || !!resourceBookingId}
            isOutlookFlow={isOutlookFlow}
            showOutlookCancelNote={isOutlookFlow && !hasVstoFlowParam}
            orderId={orderId}
            savedAsUpdate={savedAsUpdate}
            seriesOrderCount={seriesOrderCount}
        />
    )
})
